import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    generatePortalCasePdf,
    getPortalCase,
    getPortalDocuments,
    getPortalSignatureRequests
} from '../core/api/portal-service';
import { formatPlainDate } from '../core/dates/plain-date';
import { describeApiError } from '../core/errors/describe-api-error';
import { useLanguage } from '../core/hooks/use-language';
import { useTranslation } from '../core/hooks/use-translation';
import type { TranslationKey } from '../core/i18n/translation';
import type { PortalCaseDetail } from '../core/models/portal-case-detail';
import type { PortalDocument } from '../core/models/portal-document';
import type { PortalSignatureRequest } from '../core/models/portal-signature-request';
import { Alert } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { EmptyState } from '../ui/empty-state';
import { Panel } from '../ui/panel';
import { ArrowLeftIcon, CalendarIcon, CaseIcon, DocumentIcon, DownloadIcon, SignatureIcon } from '../ui/portal-icons';
import { CaseAppointmentList } from './portal/case-appointment-list';
import { CaseNextAppointment } from './portal/case-next-appointment';
import { CasePageSkeleton } from './portal/case-page-skeleton';
import { CaseProgress } from './portal/case-progress';
import { CaseStatusBanner } from './portal/case-status-banner';
import { ContactCard } from './portal/contact-card';
import { DocumentList } from './portal/document-list';
import { groupCaseAppointments } from './portal/group-case-appointments';
import { MilestoneRail } from './portal/milestone-rail';
import { toPortalCasePdfLabels } from './portal/portal-case-pdf-labels';
import { SignatureRequestList } from './portal/signature-request-list';

/**
 * One case, as its customer sees it.
 *
 * **The header card answers the customer's three questions in reading order:** what stage am I at,
 * when do I next see somebody, and is anything waiting on me. The last is the header's foot and is
 * always answered — see `CaseStatusBanner`. Everything below is the file itself.
 *
 * **Signing is a panel only while it matters.** With something to sign it leads the page; once
 * everything is signed it is history and sits below the documents — except straight after signing
 * here, where moving it would pull the list out from under the customer's pointer.
 *
 * **The page waits for all three reads before drawing.** The signing read decides both whether that
 * panel exists and what the banner says, so drawing the case first redrew both a moment later. A
 * failed documents or signing read is said on the page, never drawn as an empty list.
 *
 * Client-rendered like the dashboard: it is one customer's file behind their session.
 */
export function PortalCasePage() {
    const { t } = useTranslation();
    const language = useLanguage();
    const { clientCaseId } = useParams<{ clientCaseId: string }>();

    const [clientCase, setClientCase] = useState<PortalCaseDetail | null>(null);

    // Null is a failed read, which is not the same as having none.
    const [documents, setDocuments] = useState<PortalDocument[] | null>([]);
    const [signatureRequests, setSignatureRequests] = useState<PortalSignatureRequest[] | null>([]);
    const [signedHere, setSignedHere] = useState(false);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<TranslationKey | null>(null);
    const [printing, setPrinting] = useState(false);
    const [printFailed, setPrintFailed] = useState(false);
    const signaturesRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (clientCaseId === undefined) {
            return;
        }

        const controller = new AbortController();

        setLoading(true);
        setError(null);
        setSignedHere(false);

        Promise.all([
            getPortalCase(clientCaseId, controller.signal),
            getPortalDocuments(clientCaseId, controller.signal).catch(() => null),
            getPortalSignatureRequests(controller.signal).catch(() => null)
        ])
            .then(([loadedCase, loadedDocuments, loadedRequests]) => {
                setClientCase(loadedCase);
                setDocuments(loadedDocuments);
                setSignatureRequests(loadedRequests === null ? null : forCase(loadedRequests, clientCaseId));
                setLoading(false);
            })
            .catch((cause: unknown) => {
                if (controller.signal.aborted) {
                    return;
                }

                setClientCase(null);
                setError(describeApiError(cause));
                setLoading(false);
            });

        return () => controller.abort();
    }, [clientCaseId]);

    function reloadSignatureRequests() {
        if (clientCaseId === undefined) {
            return;
        }

        setSignedHere(true);

        getPortalSignatureRequests()
            .then((loaded) => setSignatureRequests(forCase(loaded, clientCaseId)))
            .catch(() => setSignatureRequests(null));
    }

    /** Focus moves with the scroll, so a keyboard or screen-reader user lands where the page did. */
    function reviewSignatures() {
        const panel = signaturesRef.current;

        if (panel === null) {
            return;
        }

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        panel.focus({ preventScroll: true });
    }

    /**
     * The case as a page the customer can keep or forward. The server re-reads the case rather than
     * printing what is on screen, so the page says what the file says at that moment.
     */
    async function downloadPdf(detail: PortalCaseDetail) {
        setPrinting(true);
        setPrintFailed(false);

        try {
            const { blob, fileName } = await generatePortalCasePdf(detail.id, language, toPortalCasePdfLabels(t));
            const url = URL.createObjectURL(blob);
            const link = window.document.createElement('a');

            link.href = url;
            link.download = fileName ?? `${detail.caseNumber}.pdf`;
            link.click();

            URL.revokeObjectURL(url);
        } catch {
            setPrintFailed(true);
        } finally {
            setPrinting(false);
        }
    }

    if (loading) {
        return <CasePageSkeleton />;
    }

    if (clientCase === null) {
        return (
            <div className="mx-auto max-w-2xl px-4 py-16">
                <EmptyState
                    icon={<CaseIcon />}
                    title={t(error ?? 'portal.case_not_found')}
                    action={<BackLink language={language} label={t('portal.back_to_portal')} />}
                />
            </div>
        );
    }

    const milestone = language === 'kk' ? clientCase.milestoneKk : clientCase.milestoneRu;
    const pendingSignatures =
        signatureRequests === null ? null : signatureRequests.filter((request) => !request.hasSigned).length;
    const { upcoming, earlier } = groupCaseAppointments(clientCase.appointments);
    const nextAppointment = clientCase.isClosed ? undefined : upcoming[0];

    const signaturesPanel =
        signatureRequests === null || signatureRequests.length === 0 ? null : (
            <div
                ref={signaturesRef}
                tabIndex={-1}
                className="scroll-mt-24 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950"
            >
                <Panel title={t('portal.signature_requests')} icon={<SignatureIcon />}>
                    <SignatureRequestList
                        requests={pendingFirst(signatureRequests)}
                        onSigned={reloadSignatureRequests}
                    />
                </Panel>
            </div>
        );
    const signaturesLead = (pendingSignatures ?? 0) > 0 || signedHere;

    return (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:py-12">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <BackLink language={language} label={t('portal.back_to_portal')} />

                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void downloadPdf(clientCase)}
                    disabled={printing}
                >
                    <DownloadIcon className="size-4" />
                    {printing ? t('portal.pdf.downloading') : t('portal.pdf.download')}
                </Button>
            </div>

            {printFailed && (
                <div className="mt-4">
                    <Alert>{t('portal.pdf.failed')}</Alert>
                </div>
            )}

            <Card className="mt-4 overflow-hidden">
                <div className="p-5 sm:p-7">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-slate-500 dark:text-slate-400">
                        <Badge tone={clientCase.isClosed ? 'neutral' : 'brand'}>
                            {clientCase.isClosed ? t('portal.closed') : t('portal.in_progress')}
                        </Badge>

                        <span>
                            <span className="sr-only">{t('portal.case_number')} </span>
                            <span aria-hidden="true">№ </span>
                            <span className="font-mono text-slate-700 dark:text-slate-300">
                                {clientCase.caseNumber}
                            </span>
                        </span>

                        <span aria-hidden="true" className="hidden sm:inline">·</span>
                        <span>
                            {t('portal.opened')} {formatPlainDate(clientCase.openedAt.slice(0, 10), language)}
                        </span>

                        {clientCase.closedAt !== null && (
                            <>
                                <span aria-hidden="true" className="hidden sm:inline">·</span>
                                <span>
                                    {t('portal.closed')} {formatPlainDate(clientCase.closedAt.slice(0, 10), language)}
                                </span>
                            </>
                        )}
                    </div>

                    <p className="mt-5 text-xs font-semibold tracking-wider text-brand-700 uppercase dark:text-brand-400">
                        {t('portal.stage')}
                    </p>
                    <h1
                        className={`mt-1 text-2xl font-extrabold text-balance sm:text-3xl ${
                            milestone === null
                                ? 'text-slate-500 dark:text-slate-400'
                                : 'text-slate-900 dark:text-slate-100'
                        }`}
                    >
                        {milestone ?? t('portal.no_stage')}
                    </h1>

                    {clientCase.milestones.length > 0 && (
                        <div className="mt-6">
                            <CaseProgress milestones={clientCase.milestones} isClosed={clientCase.isClosed} />
                        </div>
                    )}

                    {nextAppointment !== undefined && (
                        <div className="mt-5">
                            <CaseNextAppointment appointment={nextAppointment} />
                        </div>
                    )}
                </div>

                <div aria-live="polite">
                    <CaseStatusBanner
                        clientCase={clientCase}
                        pendingSignatures={pendingSignatures}
                        onReviewSignatures={reviewSignatures}
                    />
                </div>
            </Card>

            <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <div className="min-w-0 space-y-6">
                    {signaturesLead && signaturesPanel}

                    <Panel
                        title={t('portal.case_documents')}
                        icon={<DocumentIcon />}
                        action={documents !== null && documents.length > 0 ? <Badge>{documents.length}</Badge> : undefined}
                    >
                        {documents === null ? (
                            <Alert>{t('portal.documents_failed')}</Alert>
                        ) : (
                            <DocumentList documents={documents} />
                        )}
                    </Panel>

                    <Panel
                        title={t('portal.case_appointments')}
                        icon={<CalendarIcon />}
                        action={
                            clientCase.appointments.length > 0 ? (
                                <Badge>{clientCase.appointments.length}</Badge>
                            ) : undefined
                        }
                    >
                        <CaseAppointmentList
                            upcoming={upcoming}
                            earlier={earlier}
                            onCancelled={(appointmentId) =>
                                setClientCase((current) =>
                                    current === null
                                        ? current
                                        : {
                                              ...current,
                                              appointments: current.appointments.map((item) =>
                                                  item.id === appointmentId
                                                      ? { ...item, status: 'Cancelled', canCancel: false }
                                                      : item
                                              )
                                          }
                                )
                            }
                        />
                    </Panel>

                    {!signaturesLead && signaturesPanel}
                </div>

                <aside className="min-w-0 space-y-6">
                    {clientCase.milestones.length > 0 && (
                        <Panel title={t('portal.progress')} icon={<CaseIcon />}>
                            <MilestoneRail milestones={clientCase.milestones} />
                        </Panel>
                    )}

                    <ContactCard />
                </aside>
            </div>
        </div>
    );
}

// The signing route answers for every case the customer holds.
function forCase(requests: PortalSignatureRequest[], clientCaseId: string): PortalSignatureRequest[] {
    return requests.filter((request) => request.clientCaseId === clientCaseId);
}

function pendingFirst(requests: PortalSignatureRequest[]): PortalSignatureRequest[] {
    return [...requests].sort((left, right) => Number(left.hasSigned) - Number(right.hasSigned));
}

/** The one way back, in both the found and the not-found case — so it reads the same in each. */
function BackLink({ language, label }: { language: string; label: string }) {
    return (
        <Link
            to={`/${language}/portal`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:underline dark:text-brand-400"
        >
            <ArrowLeftIcon className="size-4" />
            {label}
        </Link>
    );
}
